/**
 * Template Store
 *
 * Manages document template state with a two-tier persistence strategy:
 * - Metadata (template names, preferences) → localStorage via zustand
 * - Binary data (actual .docx/.pptx/.xlsx files) → IndexedDB via idb-keyval
 *
 * Supports:
 * - Single user override per format (Phase 1)
 * - Multiple admin-managed templates per format (Phase 3 — gallery)
 *
 * Follows the same patterns as UIPreferencesStore.
 */

import type { TemplateFormat } from "@/config/brandTemplates";

import { create } from "zustand";
import { get as idbGet, set as idbSet, del as idbDel } from "idb-keyval";

import { logger } from "@/telemetry";

/** IndexedDB key prefix for template binary data */
const IDB_PREFIX = "template-";

/** Get the IndexedDB key for a given format */
function idbKey(format: TemplateFormat): string {
  return `${IDB_PREFIX}${format}-data`;
}

/** Get the IndexedDB key for an admin gallery template */
function idbGalleryKey(format: TemplateFormat, templateId: string): string {
  return `${IDB_PREFIX}gallery-${format}-${templateId}`;
}

/** localStorage keys for template metadata */
const LS_KEYS = {
  wordName: "template-word-name",
  pptxName: "template-pptx-name",
  xlsxName: "template-xlsx-name",
  enabled: "template-exports-enabled",
  gallery: "template-gallery-metadata",
};

export interface TemplateMetadata {
  name: string;
  size: number;
  uploadedAt: string;
}

export interface GalleryTemplate {
  id: string;
  name: string;
  format: TemplateFormat;
  size: number;
  uploadedAt: string;
  uploadedBy?: string;
}

export interface TemplateStoreState {
  /** Metadata for user-uploaded templates (one per format) */
  wordTemplate: TemplateMetadata | null;
  pptxTemplate: TemplateMetadata | null;
  xlsxTemplate: TemplateMetadata | null;
  /** Whether to use templates when available */
  useTemplateForExports: boolean;
  /** Admin-managed template gallery */
  galleryTemplates: GalleryTemplate[];

  /** Get metadata for a specific format */
  getTemplateMetadata: (format: TemplateFormat) => TemplateMetadata | null;
  /** Upload a template file (stores binary in IndexedDB, metadata in localStorage) */
  uploadTemplate: (format: TemplateFormat, file: File) => Promise<void>;
  /** Remove a user-uploaded template */
  removeTemplate: (format: TemplateFormat) => void;
  /** Retrieve template binary data from IndexedDB */
  getTemplateData: (format: TemplateFormat) => Promise<ArrayBuffer | null>;
  /** Toggle template usage preference */
  toggleUseTemplateForExports: () => void;

  /** Gallery: add admin-managed template */
  addGalleryTemplate: (format: TemplateFormat, file: File, uploadedBy?: string) => Promise<string>;
  /** Gallery: remove admin-managed template */
  removeGalleryTemplate: (templateId: string) => void;
  /** Gallery: get template binary data */
  getGalleryTemplateData: (
    format: TemplateFormat,
    templateId: string,
  ) => Promise<ArrayBuffer | null>;
  /** Gallery: get templates for a format */
  getGalleryTemplatesForFormat: (format: TemplateFormat) => GalleryTemplate[];
}

/** Load template metadata from localStorage */
function loadMetadata(format: TemplateFormat): TemplateMetadata | null {
  try {
    const keyMap: Record<TemplateFormat, string> = {
      word: LS_KEYS.wordName,
      pptx: LS_KEYS.pptxName,
      xlsx: LS_KEYS.xlsxName,
    };
    const stored = localStorage.getItem(keyMap[format]);

    if (stored) {
      return JSON.parse(stored) as TemplateMetadata;
    }

    return null;
  } catch {
    return null;
  }
}

/** Save template metadata to localStorage */
function saveMetadata(format: TemplateFormat, metadata: TemplateMetadata | null): void {
  try {
    const keyMap: Record<TemplateFormat, string> = {
      word: LS_KEYS.wordName,
      pptx: LS_KEYS.pptxName,
      xlsx: LS_KEYS.xlsxName,
    };
    const key = keyMap[format];

    if (metadata) {
      localStorage.setItem(key, JSON.stringify(metadata));
    } else {
      localStorage.removeItem(key);
    }
  } catch (error) {
    logger.warn("Failed to save template metadata to localStorage", {
      error: String(error),
    });
  }
}

/** Load gallery metadata from localStorage */
function loadGalleryMetadata(): GalleryTemplate[] {
  try {
    const stored = localStorage.getItem(LS_KEYS.gallery);

    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/** Save gallery metadata to localStorage */
function saveGalleryMetadata(templates: GalleryTemplate[]): void {
  try {
    localStorage.setItem(LS_KEYS.gallery, JSON.stringify(templates));
  } catch (error) {
    logger.warn("Failed to save gallery metadata to localStorage", {
      error: String(error),
    });
  }
}

/** Generate a unique ID for gallery templates */
function generateTemplateId(): string {
  return `tpl-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
}

export const useTemplateStore = create<TemplateStoreState>((set, get) => ({
  // Initialise from localStorage
  wordTemplate: loadMetadata("word"),
  pptxTemplate: loadMetadata("pptx"),
  xlsxTemplate: loadMetadata("xlsx"),
  useTemplateForExports: (() => {
    try {
      const stored = localStorage.getItem(LS_KEYS.enabled);

      return stored ? JSON.parse(stored) : true; // Default: templates enabled
    } catch {
      return true;
    }
  })(),
  galleryTemplates: loadGalleryMetadata(),

  getTemplateMetadata: (format: TemplateFormat): TemplateMetadata | null => {
    const state = get();

    switch (format) {
      case "word":
        return state.wordTemplate;
      case "pptx":
        return state.pptxTemplate;
      case "xlsx":
        return state.xlsxTemplate;
      default:
        return null;
    }
  },

  uploadTemplate: async (format: TemplateFormat, file: File) => {
    try {
      const arrayBuffer = await file.arrayBuffer();

      // Store binary data in IndexedDB
      await idbSet(idbKey(format), arrayBuffer);

      // Store metadata in localStorage + zustand
      const metadata: TemplateMetadata = {
        name: file.name,
        size: file.size,
        uploadedAt: new Date().toISOString(),
      };

      saveMetadata(format, metadata);

      const update: Partial<TemplateStoreState> = {};

      if (format === "word") update.wordTemplate = metadata;
      else if (format === "pptx") update.pptxTemplate = metadata;
      else if (format === "xlsx") update.xlsxTemplate = metadata;

      set(update);

      logger.info(`Template uploaded: ${format} — ${file.name} (${file.size} bytes)`);
    } catch (error) {
      logger.error(`Failed to upload ${format} template`, { error: String(error) });
      throw error;
    }
  },

  removeTemplate: (format: TemplateFormat) => {
    // Remove from IndexedDB (fire-and-forget)
    idbDel(idbKey(format)).catch((error) => {
      logger.warn(`Failed to remove ${format} template from IndexedDB`, {
        error: String(error),
      });
    });

    // Remove metadata
    saveMetadata(format, null);

    const update: Partial<TemplateStoreState> = {};

    if (format === "word") update.wordTemplate = null;
    else if (format === "pptx") update.pptxTemplate = null;
    else if (format === "xlsx") update.xlsxTemplate = null;

    set(update);

    logger.info(`Template removed: ${format}`);
  },

  getTemplateData: async (format: TemplateFormat): Promise<ArrayBuffer | null> => {
    try {
      const data = await idbGet<ArrayBuffer>(idbKey(format));

      return data ?? null;
    } catch (error) {
      logger.warn(`Failed to read ${format} template from IndexedDB`, {
        error: String(error),
      });

      return null;
    }
  },

  toggleUseTemplateForExports: () => {
    const newValue = !get().useTemplateForExports;

    set({ useTemplateForExports: newValue });

    try {
      localStorage.setItem(LS_KEYS.enabled, JSON.stringify(newValue));
    } catch (error) {
      logger.warn("Failed to save template preference to localStorage", {
        error: String(error),
      });
    }
  },

  // ── Gallery Methods ──

  addGalleryTemplate: async (
    format: TemplateFormat,
    file: File,
    uploadedBy?: string,
  ): Promise<string> => {
    const id = generateTemplateId();

    try {
      const arrayBuffer = await file.arrayBuffer();

      // Store binary in IndexedDB
      await idbSet(idbGalleryKey(format, id), arrayBuffer);

      // Add metadata
      const template: GalleryTemplate = {
        id,
        name: file.name,
        format,
        size: file.size,
        uploadedAt: new Date().toISOString(),
        uploadedBy,
      };

      const updated = [...get().galleryTemplates, template];

      saveGalleryMetadata(updated);
      set({ galleryTemplates: updated });

      logger.info(`Gallery template added: ${format} — ${file.name} (id: ${id})`);

      return id;
    } catch (error) {
      logger.error(`Failed to add gallery template ${format}`, { error: String(error) });
      throw error;
    }
  },

  removeGalleryTemplate: (templateId: string) => {
    const template = get().galleryTemplates.find((t) => t.id === templateId);

    if (!template) return;

    // Remove binary from IndexedDB (fire-and-forget)
    idbDel(idbGalleryKey(template.format, templateId)).catch((error) => {
      logger.warn(`Failed to remove gallery template ${templateId} from IndexedDB`, {
        error: String(error),
      });
    });

    // Remove metadata
    const updated = get().galleryTemplates.filter((t) => t.id !== templateId);

    saveGalleryMetadata(updated);
    set({ galleryTemplates: updated });

    logger.info(`Gallery template removed: ${templateId}`);
  },

  getGalleryTemplateData: async (
    format: TemplateFormat,
    templateId: string,
  ): Promise<ArrayBuffer | null> => {
    try {
      const data = await idbGet<ArrayBuffer>(idbGalleryKey(format, templateId));

      return data ?? null;
    } catch (error) {
      logger.warn(`Failed to read gallery template ${templateId} from IndexedDB`, {
        error: String(error),
      });

      return null;
    }
  },

  getGalleryTemplatesForFormat: (format: TemplateFormat): GalleryTemplate[] => {
    return get().galleryTemplates.filter((t) => t.format === format);
  },
}));
